/**
 * Starter BOQ templates: the list, and creating a new (inactive) one.
 *
 * Authoring lives here rather than in a seeder so production templates can be
 * written against the real catalogue and revised without a deploy (#416).
 */

import { Button } from "@shared-ui/button";
import { Input } from "@shared-ui/input";
import { Label } from "@shared-ui/label";
import { Skeleton } from "@shared-ui/skeleton";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Dialog } from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { useAdminAuthStore } from "@/features/auth/store/admin-auth.store";
import { DataTable } from "@/features/catalog/components/data-table";
import { apiErrorMessage } from "@/lib/api-error";
import { hasPermission } from "@/lib/permissions";
import { slugify } from "../lib/rules";
import {
	createTemplate,
	listTemplates,
	type TemplateSummary,
} from "../services/boq-templates-api.service";

export const TEMPLATES_QUERY_KEY = ["admin-boq-templates"];

export function BoqTemplatesDashboardPage() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const operator = useAdminAuthStore((s) => s.operator);
	const canWrite = hasPermission(operator, "admin:boq-templates:write");
	const [creating, setCreating] = useState(false);

	const query = useQuery({
		queryKey: TEMPLATES_QUERY_KEY,
		queryFn: listTemplates,
	});

	return (
		<div className="grid gap-5">
			<div className="flex items-start justify-between gap-4">
				<div className="grid gap-2">
					<p className="text-xs font-semibold uppercase tracking-[0.16em] text-amber-700">
						Catalogue
					</p>
					<h2 className="text-2xl font-semibold tracking-tight">
						Starter BOQ templates
					</h2>
					<p className="text-sm text-muted-foreground">
						Only active templates are offered to designers and contractors.
						Every line points at a catalogue product.
					</p>
				</div>
				{canWrite && (
					<Button onClick={() => setCreating(true)}>New template</Button>
				)}
			</div>

			{query.isLoading ? (
				<Skeleton className="h-40 w-full" />
			) : query.isError ? (
				<p role="alert" className="text-sm text-red-700">
					Could not load templates.
				</p>
			) : (query.data ?? []).length === 0 ? (
				<EmptyState
					title="No starter templates yet"
					hint={
						canWrite
							? "Create one, add sections and lines from the catalogue, then activate it."
							: "Nobody has created one yet."
					}
				/>
			) : (
				<DataTable
					rows={query.data ?? []}
					rowKey={(t) => t.id}
					onRowActivate={(t) => navigate(`/boq-templates/${t.id}`)}
					rowLabel={(t) => `Edit ${t.name}`}
					columns={[
						{ header: "Name", cell: (t) => t.name },
						{ header: "Slug", cell: (t) => t.slug, cellClassName: "font-mono" },
						{
							header: "Scope",
							cell: (t) => t.projectScope?.replace("_", " ") ?? "any",
							cellClassName: "capitalize",
						},
						{
							header: "For",
							cell: (t) => t.supportedProviderRoles.join(", "),
						},
						{
							header: "Sections / lines",
							cell: (t) => `${t.sectionCount} / ${t.itemCount}`,
							cellClassName: "tabular-nums",
						},
						{
							header: "Status",
							cell: (t) => <Badge>{t.isActive ? "Active" : "Inactive"}</Badge>,
						},
					]}
				/>
			)}

			{creating && (
				<CreateDialog
					onClose={() => setCreating(false)}
					onCreated={(t) => {
						void queryClient.invalidateQueries({
							queryKey: TEMPLATES_QUERY_KEY,
						});
						navigate(`/boq-templates/${t.id}`);
					}}
				/>
			)}
		</div>
	);
}

function CreateDialog({
	onClose,
	onCreated,
}: {
	onClose: () => void;
	onCreated: (t: TemplateSummary) => void;
}) {
	const [name, setName] = useState("");
	const [slug, setSlug] = useState("");
	const [slugTouched, setSlugTouched] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const create = useMutation({
		mutationFn: () => createTemplate({ name: name.trim(), slug }),
		onSuccess: onCreated,
		onError: (e) => setError(apiErrorMessage(e, "Could not create template")),
	});

	return (
		<Dialog open titleId="new-boq-template-title" onClose={onClose}>
			<h3 id="new-boq-template-title" className="text-lg font-semibold">
				New starter template
			</h3>
			<p className="text-sm text-muted-foreground">
				It starts inactive. Add sections and lines, then switch it on.
			</p>
			<div className="grid gap-1">
				<Label htmlFor="tpl-name">Name</Label>
				<Input
					id="tpl-name"
					value={name}
					onChange={(e) => {
						setName(e.target.value);
						if (!slugTouched) setSlug(slugify(e.target.value));
					}}
				/>
			</div>
			<div className="grid gap-1">
				<Label htmlFor="tpl-slug">Slug</Label>
				<Input
					id="tpl-slug"
					value={slug}
					onChange={(e) => {
						setSlugTouched(true);
						setSlug(e.target.value);
					}}
				/>
			</div>
			{error && (
				<p role="alert" className="text-sm text-red-700">
					{error}
				</p>
			)}
			<div className="flex justify-end gap-2">
				<Button variant="outline" onClick={onClose}>
					Cancel
				</Button>
				<Button
					disabled={!name.trim() || !slug || create.isPending}
					onClick={() => {
						setError(null);
						create.mutate();
					}}
				>
					Create
				</Button>
			</div>
		</Dialog>
	);
}
